import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { SyncRequestsPage } from '@features/admin/sync-requests';
import { useAdminSyncRequestStore } from '@features/admin/sync-requests/useAdminSyncRequestStore.ts';

const syncTypeSchema = z.enum([
  'PORTAL',
  'CATEGORY',
  'PRODUCT',
  'ORGANIZATION',
  'SUPPLIER',
  'PARAMETER_NAME',
  'PARAMETER_VALUE',
  'PRICE',
  'PRICE_VARIANT',
  'CATALOG_DATA',
  'AUCTION',
  'PRICE_VISIBILITY',
  'ADDRESS',
  'DELIVERIES',
  'DELIVERY',
  'DELIVERY_CONTACTS',
  'DELIVERY_DAYS',
  'CONTRACT_GROUP',
  'USER',
  'ORGANIZATION_USER',
]);

const adminSyncRequestsSearchSchema = z.object({
  syncType: syncTypeSchema.optional(),
  actionType: z.enum(['UPSERT', 'DELETE']).optional(),
  status: z.array(z.enum(['PENDING', 'COMPLETED', 'WARNING', 'FAILED'])).optional().default(['PENDING', 'WARNING', 'FAILED']),
  jsonSearch: z.string().optional(),
  // Přesná shoda na request_id (dávku). Na rozdíl od jsonSearch, který hledá GUIDy uvnitř payloadu.
  requestId: z.uuid().optional(),
  // Autor ze zdrojového systému — substring, ILIKE (hodnota je uložená i s doménou).
  author: z.string().optional(),
  // Pozor: ne z.coerce.boolean() — Boolean('false') je true, takže by filtr z URL nešel vypnout.
  hideIgnored: z
    .boolean()
    .or(z.enum(['true', 'false']).transform(v => v === 'true'))
    .optional()
    .default(false),
  page: z.coerce.number().optional().default(1),
  size: z.coerce.number().optional().default(20),
  // PROCESSING_ORDER = pořadí, v jakém věty bere worker (výchozí); ID = pořadí příchodu, jde otočit.
  // Schválně bez `.default()` — to by z nich udělalo povinná pole výsledného typu a každé
  // `navigate({ search })` odjinud (np-load, sync-send, filtry) by je muselo vypisovat.
  // Výchozí hodnota se dosazuje až při čtení, viz `beforeLoad` a `sortIcon`.
  sortBy: z.enum(['PROCESSING_ORDER', 'ID']).optional(),
  sortDir: z.enum(['ASC', 'DESC']).optional(),
});

export type AdminSyncRequestsSearch = z.infer<typeof adminSyncRequestsSearchSchema>;

export const Route = createFileRoute('/admin/sync-requests/')({
  validateSearch: search => adminSyncRequestsSearchSchema.parse(search),
  component: RouteComponent,
  beforeLoad: ctx => {
    const { syncType, actionType, status, jsonSearch, requestId, hideIgnored, author, page, size, sortBy, sortDir } = ctx.search;
    useAdminSyncRequestStore.getState().loadSyncRequests({
      syncType,
      actionType,
      status,
      jsonSearch,
      requestId,
      hideIgnored,
      author,
      page: page || 1,
      size: size || 20,
      sortBy: sortBy || 'PROCESSING_ORDER',
      sortDir: sortDir || 'ASC',
    });
    // Souhrn dávky se načítá jen když je filtr na request_id aktivní (jinak dávka není určená).
    useAdminSyncRequestStore.getState().loadBatch(requestId);
  },
});

function RouteComponent() {
  return <SyncRequestsPage />;
}
